import { useContext, useEffect, useRef } from "react";
import { v4 as uuidv4 } from "uuid";

import api from "../../services/api";
import isNativeApp from "../../utils/isNativeApp";
import { onPushAction } from "../../services/capacitorInit";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import toastError from "../../errors/toastError";

/**
 * usePushNotifications
 * Registers for push notifications when running inside Capacitor (iOS/Android).
 * On web, this hook is a no-op — the existing Socket.io notification system
 * handles in-browser alerts and nothing here interferes with it.
 *
 * DUAL-PATH: this hook ships from the LIVE web and executes inside every
 * installed app binary, old and new, so it must speak to whichever native
 * push plugin the binary actually contains — chosen via
 * Capacitor.isPluginAvailable(), which reads the NATIVE plugin registry:
 *
 *  - FIREBASE binaries (unified iOS/Android builds): the FirebaseMessaging
 *    native plugin (@capacitor-firebase/messaging). Reached through
 *    registerPlugin("FirebaseMessaging") from @capacitor/core — deliberately
 *    NOT through the plugin's npm package, which is not a dependency of this
 *    web tree; the proxy routes every call to the native implementation, and
 *    the token it yields is an FCM token the backend (firebase-admin) can
 *    deliver to on BOTH platforms. This is what turns iOS push on.
 *  - LEGACY binaries (pre-unification Android / App Store iOS): the classic
 *    @capacitor/push-notifications flow, byte-compatible with the previously
 *    deployed hook (permission → listeners → register, token via the
 *    'registration' event). Old Android keeps its working FCM path untouched.
 *
 * Flow (shared):
 *  1. isNativeApp() gate — skip entirely on web (both isPluginAvailable calls
 *     would also return false on web; belt and suspenders)
 *  2. Wait for a logged-in user (user.id) — the iOS permission prompt must
 *     never appear on the login screen, and a failed registration must never
 *     surface an error at launch (Apple Guideline 2.1 — rejection history)
 *  3. Token is POSTed to /auth/device-token via the shared axios instance so
 *     the Authorization header is attached by the singleton interceptor
 *  4. Notification-tap navigation:
 *      - legacy: capacitorInit's global boot listener buffers the tap (armed
 *        at boot so a cold-start deep link survives); we subscribe via
 *        onPushAction; navigation goes through TicketsContext's
 *        navigateToTicket (verify-first, back-stack parity) — audit #11.
 *      - firebase: our own notificationActionPerformed listener; the native
 *        plugin retains the event until consumed (retainUntilConsumed), so a
 *        cold-start tap still reaches the post-login listener.
 *
 * All plugin modules are imported lazily inside the native-only branch, so
 * the production web bundle never has to resolve them at module-eval time.
 * Errors are logged to console only — never toasted (Apple Guideline 2.1).
 */
const usePushNotifications = (user, history) => {
  const registered = useRef(false);
  // Context navigation gives push-taps the same WhatsApp back-stack parity
  // as in-app ticket switches (chat→chat replaces instead of pushing). A
  // ref holds the latest instance so the long-lived native listeners never
  // capture a stale closure and the effect never has to re-subscribe.
  const { navigateToTicket } = useContext(TicketsContext);
  const navigateRef = useRef(navigateToTicket);
  navigateRef.current = navigateToTicket;
  // Monotonic tap counter: the legacy fallback awaits a network fetch, so
  // an older slow tap must never navigate over a newer one (latest-wins,
  // same pattern as Ticket/index.js currentTicketIdRef).
  const tapSeqRef = useRef(0);

  useEffect(() => {
    if (!user?.id || registered.current) return;
    if (!isNativeApp()) return; // web — do nothing

    // Listener handles we created here, removed individually on cleanup so we
    // never tear down the global tap listener registered by capacitorInit.
    const handles = [];
    // Flipped by the effect cleanup (logout/unmount): an in-flight legacy
    // fetch must not navigate a dead session (stale cross-company ticket).
    let cancelled = false;
    // Navigate on a tapped notification — handles both the cold-start buffered
    // tap and live taps while the app runs (legacy binaries; no-op on firebase
    // binaries where capacitorInit's legacy listener could not attach).
    // ONE tap handler for both plugin families. The route is
    // /tickets/:uuid, so prefer the uuid from the payload; a legacy
    // numeric-only payload (old binaries / notifications queued before
    // the payload change) is resolved via GET /tickets/:id first.
    const openFromPush = async (data) => {
      const uuid = data?.ticketUuid;
      const id = data?.ticketId;
      const seq = ++tapSeqRef.current;
      try {
        if (!id) {
          // Not produced by our backend today (ticketId is always set) -
          // kept as a safety net for foreign payloads.
          if (uuid) navigateRef.current({ id: undefined, uuid, code: uuidv4() });
          return;
        }
        // Verify-first: one cheap GET confirms the ticket still exists and
        // belongs to this company BEFORE navigating, so a stale notification
        // (deleted / cross-company ticket) toasts and stays put instead of
        // landing on an inert chat. It also resolves the uuid for legacy
        // numeric-only payloads. Costs one round-trip per tap - taps are
        // rare and the request races the app coming to the foreground.
        const { data: ticket } = await api.get(`/tickets/${id}`);
        // Post-await guards: the user may have logged out (effect cleaned
        // up) or tapped a NEWER notification while this fetch was in flight.
        if (cancelled || seq !== tapSeqRef.current) return;
        if (ticket?.uuid) {
          navigateRef.current({ id: ticket.id, uuid: ticket.uuid, code: uuidv4() });
        }
      } catch (err) {
        // Stay on the current screen — navigating to a numeric route would
        // just land on a broken ticket page. Toast so the tap is not a
        // silent no-op for the user.
        if (!cancelled) toastError(err);
        console.error("[Push] tap navigation failed:", err);
      }
    };

    const unsubscribeAction = onPushAction(openFromPush);

    const postToken = async (value, Capacitor) => {
      try {
        await api.post("/auth/device-token", {
          token: value,
          platform: Capacitor.getPlatform(), // 'ios' | 'android'
        });
      } catch (err) {
        console.error("[Push] Failed to register token:", err);
      }
    };

    // FIREBASE binaries — FCM token on both platforms (on iOS the native SDK
    // maps the APNs token to an FCM token internally).
    const initFirebase = async (Capacitor, registerPlugin) => {
      const FirebaseMessaging = registerPlugin("FirebaseMessaging");

      // Listeners BEFORE requestPermissions/getToken so the first token event
      // cannot be missed (same principle as audit #12).
      handles.push(
        await FirebaseMessaging.addListener("tokenReceived", (event) => {
          if (event?.token) postToken(event.token, Capacitor);
        })
      );
      handles.push(
        await FirebaseMessaging.addListener(
          "notificationActionPerformed",
          (action) => {
            openFromPush(action?.notification?.data);
          }
        )
      );

      const perm = await FirebaseMessaging.requestPermissions();
      if (perm.receive !== "granted") {
        console.log("[Push] Permission denied");
        return;
      }

      // getToken() triggers native registration and resolves with the FCM
      // token once the APNs/FCM handshake completes.
      const { token } = await FirebaseMessaging.getToken();
      if (token) postToken(token, Capacitor);
    };

    // LEGACY binaries — byte-compatible with the previously deployed hook.
    const initLegacy = async (Capacitor) => {
      const { PushNotifications } = await import(
        "@capacitor/push-notifications"
      );

      // 1. Request permission — only reachable post-login (user.id gate)
      const permResult = await PushNotifications.requestPermissions();
      if (permResult.receive !== "granted") {
        console.log("[Push] Permission denied");
        return;
      }

      // 2. Attach listeners BEFORE register() (audit #12). userId/companyId
      // are derived from the JWT on the server — only token + platform go in
      // the body.
      handles.push(
        await PushNotifications.addListener("registration", (token) => {
          if (token?.value) postToken(token.value, Capacitor);
        })
      );
      handles.push(
        await PushNotifications.addListener("registrationError", (err) => {
          console.error("[Push] Registration error:", err);
        })
      );
      // Foreground notification — the Socket.io system already shows in-app
      // alerts, so we intentionally do nothing here to avoid duplicates.
      handles.push(
        await PushNotifications.addListener(
          "pushNotificationReceived",
          (notification) => {
            console.log("[Push] Foreground notification:", notification.title);
          }
        )
      );

      // 3. Register — listeners are now armed.
      await PushNotifications.register();
    };

    const init = async () => {
      try {
        const { Capacitor, registerPlugin } = await import("@capacitor/core");

        registered.current = true;

        if (Capacitor.isPluginAvailable("FirebaseMessaging")) {
          await initFirebase(Capacitor, registerPlugin);
        } else if (Capacitor.isPluginAvailable("PushNotifications")) {
          await initLegacy(Capacitor);
        } else {
          console.log("[Push] No push plugin available in this binary");
        }
      } catch (err) {
        console.error("[Push] Unexpected error:", err);
      }
    };

    init();

    return () => {
      // Cleanup on unmount (e.g. logout): remove only the listeners we added,
      // leaving capacitorInit's global tap listener intact.
      cancelled = true;
      unsubscribeAction();
      handles.forEach((h) => {
        try {
          h.remove();
        } catch (_e) {
          /* ignore */
        }
      });
      registered.current = false;
    };
  }, [user?.id, history]);
};

export default usePushNotifications;
